import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import { Section } from "./Section";
import AIButton from "./AIButton";
import { polishText } from "@/lib/api";

export default function ExperienceSection({ resume, setResume, open, onToggle }) {
  const [polishing, setPolishing] = useState({});

  const add = () =>
    setResume((r) => ({
      ...r,
      experience: [
        ...r.experience,
        { id: crypto.randomUUID(), company: "", role: "", location: "", startDate: "", endDate: "", current: false, bullets: [""] },
      ],
    }));
  const update = (i, k, v) =>
    setResume((r) => {
      const next = [...r.experience];
      next[i] = { ...next[i], [k]: v };
      return { ...r, experience: next };
    });
  const remove = (i) =>
    setResume((r) => ({ ...r, experience: r.experience.filter((_, idx) => idx !== i) }));
  const addBullet = (i) =>
    setResume((r) => {
      const next = [...r.experience];
      next[i] = { ...next[i], bullets: [...next[i].bullets, ""] };
      return { ...r, experience: next };
    });
  const updateBullet = (i, bi, v) =>
    setResume((r) => {
      const next = [...r.experience];
      next[i] = { ...next[i], bullets: next[i].bullets.map((b, idx) => (idx === bi ? v : b)) };
      return { ...r, experience: next };
    });
  const removeBullet = (i, bi) =>
    setResume((r) => {
      const next = [...r.experience];
      next[i] = { ...next[i], bullets: next[i].bullets.filter((_, idx) => idx !== bi) };
      return { ...r, experience: next };
    });

  const polishBullet = async (expIdx, bulletIdx) => {
    const text = resume.experience[expIdx].bullets[bulletIdx];
    if (!text?.trim()) {
      toast.error("Write a bullet first");
      return;
    }
    const key = `exp-${expIdx}-b-${bulletIdx}`;
    setPolishing((p) => ({ ...p, [key]: true }));
    try {
      const out = await polishText({
        text,
        kind: "bullet",
        role: resume.experience[expIdx].role,
      });
      setResume((r) => {
        const next = { ...r, experience: [...r.experience] };
        next.experience[expIdx] = {
          ...next.experience[expIdx],
          bullets: next.experience[expIdx].bullets.map((b, i) => (i === bulletIdx ? out : b)),
        };
        return next;
      });
      toast.success("Bullet polished");
    } catch (e) {
      toast.error("AI polish failed");
    } finally {
      setPolishing((p) => ({ ...p, [key]: false }));
    }
  };

  return (
    <Section id="experience" title="Work experience" open={open} onToggle={onToggle}>
      <div className="space-y-5">
        {resume.experience.map((exp, i) => (
          <div key={exp.id} className="border border-black/10 p-4 space-y-3" data-testid={`experience-item-${i}`}>
            <div className="flex items-center justify-between">
              <div className="label-mono">Position {i + 1}</div>
              {resume.experience.length > 1 && (
                <button onClick={() => remove(i)} className="text-neutral-400 hover:text-[#FF2A00] transition-colors" data-testid={`remove-experience-${i}`}>
                  <Trash size={14} weight="bold" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input className="field-input" placeholder="Company" value={exp.company} onChange={(e) => update(i, "company", e.target.value)} data-testid={`exp-company-${i}`} />
              <input className="field-input" placeholder="Role" value={exp.role} onChange={(e) => update(i, "role", e.target.value)} data-testid={`exp-role-${i}`} />
              <input className="field-input" placeholder="Location" value={exp.location} onChange={(e) => update(i, "location", e.target.value)} data-testid={`exp-location-${i}`} />
              <div className="grid grid-cols-2 gap-2">
                <input className="field-input" placeholder="Start (e.g. 2021)" value={exp.startDate} onChange={(e) => update(i, "startDate", e.target.value)} data-testid={`exp-start-${i}`} />
                <input className="field-input" placeholder="End / Present" value={exp.endDate} onChange={(e) => update(i, "endDate", e.target.value)} data-testid={`exp-end-${i}`} />
              </div>
            </div>

            <div className="pt-2">
              <div className="label-mono mb-2">Achievements / bullets</div>
              <div className="space-y-2">
                {exp.bullets.map((b, bi) => (
                  <div key={`${exp.id}-b-${bi}`} className="flex items-start gap-2">
                    <span className="mt-3 h-1.5 w-1.5 rounded-full bg-[#2400FF] shrink-0" />
                    <textarea
                      className="field-textarea min-h-[52px]"
                      placeholder="Started with a strong verb, quantified impact..."
                      value={b}
                      onChange={(e) => updateBullet(i, bi, e.target.value)}
                      data-testid={`exp-bullet-${i}-${bi}`}
                    />
                    <div className="flex flex-col gap-1 pt-1">
                      <AIButton
                        onClick={() => polishBullet(i, bi)}
                        loading={!!polishing[`exp-${i}-b-${bi}`]}
                        testid={`polish-bullet-${i}-${bi}`}
                        label="Polish"
                      />
                      {exp.bullets.length > 1 && (
                        <button
                          onClick={() => removeBullet(i, bi)}
                          className="text-[10px] font-mono uppercase tracking-widest text-neutral-400 hover:text-[#FF2A00] px-2 py-1 border border-black/10"
                          data-testid={`remove-bullet-${i}-${bi}`}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <button onClick={() => addBullet(i)} className="text-xs font-mono uppercase tracking-widest text-[#2400FF] flex items-center gap-1 hover:underline" data-testid={`add-bullet-${i}`}>
                  <Plus size={12} weight="bold" /> Add bullet
                </button>
              </div>
            </div>
          </div>
        ))}
        <button onClick={add} className="btn-secondary text-xs px-4 py-2" data-testid="add-experience-button">
          <Plus size={14} weight="bold" /> Add position
        </button>
      </div>
    </Section>
  );
}
